import { useMemo, useCallback, useRef } from 'react'

import type { QueryClient } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { cloneDeep } from 'lodash-es'
import type { Id as ToastId } from 'react-toastify'
import { toast } from 'react-toastify'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import type {
  AuctionUpdates,
  UpdateAuctionDataFunction,
} from '@b-stock/fe-core/bstock-next/helpers'
import { use3MPAuctionUpdates } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedAuction,
  OrchestratedAuctionListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { Countdown, FormattedMessage } from '@b-stock/fe-core/bstock-react'

import useAccountId from '@/buyer-portal/helpers/useAccountId'
import usePusher from '@/buyer-portal/helpers/usePusher'
import listingDetailsQuery from '@/queries/listingDetailsQuery'

const usePollingSnackbar = () => {
  const unavailableToastId = useRef<ToastId | null>(null)
  const onManualPollScheduled = useCallback(
    (nextManualPollUpdate: Date | null) => {
      if (nextManualPollUpdate) {
        const render = () => (
          <div>
            <FormattedMessage
              id="Auction.details.pusherUnavailable"
              values={{
                countdown: <Countdown target={nextManualPollUpdate} />,
              }}
            />
          </div>
        )
        if (unavailableToastId.current) {
          toast.update(unavailableToastId.current, {
            render,
          })
        } else {
          unavailableToastId.current = toast(render)
        }
      } else {
        if (unavailableToastId.current) {
          toast.dismiss(unavailableToastId.current)
        }
        unavailableToastId.current = null
      }
    },
    [unavailableToastId]
  )
  return {
    onManualPollScheduled,
  }
}

type MergedAuction = OrchestratedAuction & {
  // These two values are on LimitedAuction but not Auction...
  // but there's no harm in setting them if we happened to get an Auction
  userBidAmount?: number
  winning?: boolean
  // canceledTime only exists on the internal Auction shape, never on the
  // LimitedAuction buyers receive from either the initial fetch or any
  // Pusher event — so it's set locally below when we observe the transition.
  canceledTime?: string
}

const mergeLimitedUpdate = (
  updates: Readonly<
    AuctionUpdates & ({ type: 'status' } | { type: 'private' })
  >,
  mergedAuction: MergedAuction
) => {
  // It's one of the other updates, so pull out the fields into the correct location
  const mergedBid =
    updates.type === 'status' ? mergedAuction.bid : updates.data.bid
  mergedAuction.bidStatus = updates.data.bidStatus
  mergedAuction.winning = updates.data.winning
  if (mergedBid) {
    mergedBid.winning = updates.data.winning
  }

  if (updates.type === 'status' && typeof updates.data.closed === 'boolean') {
    mergedAuction.closed = updates.data.closed
    if (mergedBid) {
      mergedBid.auction.closed = updates.data.closed
    }
  }
  if (updates.type === 'private') {
    mergedAuction.userBidAmount = updates.data.userBidAmount
  }
  mergedAuction.bid = mergedBid
}

const getUpdateAuctionData = (
  queryKey: unknown[],
  queryClient: QueryClient,
  listingId: string
): UpdateAuctionDataFunction => {
  return (updates: AuctionUpdates) => {
    const applyUpdate = (
      oldListingDetails: OrchestratedAuctionListingDetails | undefined
    ): OrchestratedAuctionListingDetails | undefined => {
      if (!oldListingDetails?.auction) {
        return undefined
      }

      let mergedAuction: MergedAuction = cloneDeep(oldListingDetails.auction)
      if (updates.type === 'public' || updates.type === 'poll') {
        const isNewlyCanceled = updates.data.canceled && !mergedAuction.canceled

        // Both Public and Polling updates should include a subset of the OrchestratedAuction fields,
        // so we can just apply the data from those updates directly
        mergedAuction = {
          ...mergedAuction,
          ...updates.data,
          // Handle nullable price fields
          startPrice: updates.data.startPrice ?? mergedAuction.startPrice ?? 0,
          winningBidAmount:
            updates.data.winningBidAmount ??
            mergedAuction.winningBidAmount ??
            0,
          nextMinBidAmount:
            updates.data.nextMinBidAmount ??
            mergedAuction.nextMinBidAmount ??
            0,
        }

        // The update payload never carries a cancellation timestamp, so stamp
        // one locally the moment we observe the active -> canceled transition
        // rather than leaving the display with no date at all.
        if (isNewlyCanceled && !mergedAuction.canceledTime) {
          mergedAuction.canceledTime = new Date().toISOString()
        }
      } else {
        mergeLimitedUpdate(updates, mergedAuction)
      }

      return {
        ...oldListingDetails,
        auction: mergedAuction,
      } satisfies OrchestratedAuctionListingDetails
    }

    const updatedListing =
      queryClient.setQueryData<OrchestratedAuctionListingDetails>(
        queryKey,
        applyUpdate
      )

    // Also sync searchListingDetailsQuery so inline bid forms (list view)
    // receive the same real-time auction data (e.g. actualEndTime after popcorn extension)
    queryClient.setQueriesData<OrchestratedAuctionListingDetails>(
      { queryKey: ['searchListingDetails', listingId] },
      applyUpdate
    )

    return updatedListing?.auction
  }
}

const useAuctionDetailsUpdates = ({
  _id: listingId,
  auction,
}: OrchestratedAuctionListingDetails) => {
  const accessToken = useAccessToken()
  const accountId = useAccountId()
  const queryClient = useQueryClient()
  const pusherClient = usePusher()

  const updateAuctionData = useMemo(() => {
    const queryKey = listingDetailsQuery.getKey(accessToken, [
      listingId,
      accountId,
    ])
    return getUpdateAuctionData(queryKey, queryClient, listingId)
  }, [accessToken, listingId, accountId, queryClient])

  const { onManualPollScheduled } = usePollingSnackbar()

  use3MPAuctionUpdates({
    listingId,
    viewerAccountId: accountId,
    auction,
    updateAuctionData,
    onManualPollScheduled,
    pusherClient,
  })
}

export default useAuctionDetailsUpdates
